import { useQuery, keepPreviousData } from '@tanstack/react-query';
import useReduxStateMarket, { MarketPageType } from './useReduxStateMarkets';
import useIndustryGetter from './useIndustryGetter';
import {
  getMarketPerformanceKey,
  queryFn,
  responseTransformer,
} from '../query/fetchMarketPerformance';
import type { PeriodType } from '../query/fetchMarketPerformance';
import { useMemo } from 'react';

const defaultAverages = {
  '1Y': 0,
  '3Y': 0,
  '5Y': 0,
  '10Y': 0,
};

const getDefault = (): ReturnType<typeof responseTransformer> => ({
  chartData: [],
  industry: undefined,
  market: '',
  averages: {
    ps: defaultAverages,
    medianPE: defaultAverages,
    absolutePE: defaultAverages,
  },
});

export function useMarketPerformance(period: PeriodType) {
  const { market, marketPageType } = useReduxStateMarket();
  const [{ id }, enabled] = useIndustryGetter();
  const { data: marketPerf, status } = useQuery({
    queryKey: getMarketPerformanceKey(market, id, period),
    queryFn,
    staleTime: Infinity,
    enabled: marketPageType === MarketPageType.MARKET ? true : enabled,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });

  const transformedData = useMemo(() => {
    if (typeof marketPerf === 'undefined') {
      return getDefault();
    }
    return responseTransformer(marketPerf.IndustryTimeseries);
  }, [marketPerf]);

  return { status, data: transformedData };
}

export type MarketPerformance = NonNullable<
  ReturnType<typeof useMarketPerformance>['data']
>;

export type ChartData = MarketPerformance['chartData'];

export type { PeriodType };
